import React, { useState, useEffect } from 'react'
import {
    Stack,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    MenuItem,
    Divider,
    Snackbar,
    Alert
} from '@mui/material'
import { getActiveEmployees } from '../../services/EmployeeService'
import { createAssignItem, getActiveCategories, getItemAvailable } from '../../services/InventoryService'

const ItemAssignment = () => {
    const [employees, setEmployees] = useState([])
    const [categories, setCategories] = useState([])
    const [items, setItems] = useState([])

    const [form, setForm] = useState({
        employeeId: '',
        category: '',
        itemId: '',
        assignmentDate: new Date().toISOString().slice(0, 10),
        location: '',
        remarks: ''
    })

    const [loading, setLoading] = useState(false)
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

    useEffect(() => {
        loadEmployees()
        loadCategories()
    }, [])

    useEffect(() => {
        if (!form.category) return
        setForm((prev) => ({ ...prev, itemId: '' }))
        loadItems(form.category)
    }, [form.category])

    const loadEmployees = async () => {
        try {
            const result = await getActiveEmployees()
            setEmployees(result || [])
        } catch (err) {
            console.error(err)
        }
    }

    const loadCategories = async () => {
        try {
            const result = await getActiveCategories()
            setCategories(result || [])
        } catch (err) {
            console.error(err)
        }
    }

    const loadItems = async (categoryName) => {
        if (!categoryName) return
        try {
            const result = await getItemAvailable({ category: categoryName })
            setItems(result || [])
        } catch (err) {
            console.error(err)
        }
    }

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }))
    }

    const handleSubmit = async () => {
        const selectedEmployee = employees.find((e) => e.employeeId === form.employeeId)
        const selectedItem = items.find((i) => i.id === form.itemId)
        console.log('Submitting assignment', { form, selectedEmployee, selectedItem })
        if (!selectedItem) {
            setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' })
            return
        }

        if (!form.employeeId && !form.location) {
            setSnackbar({ open: true, message: 'Please select employee or the location', severity: 'error' })
            return
        }

        if (selectedItem.quantity <= 0) {
            setSnackbar({ open: true, message: 'Item out of stock', severity: 'error' })
            return
        }

        // Optimistic stock update
        selectedItem.quantity -= 1

        const payload = {
            employeeId: form.employeeId,
            itemId: selectedItem.id,
            quantity: 1,
            assignDate: form.assignmentDate,
            location: form.location,
            remarks: form.remarks
        }

        try {
            setLoading(true)
            const result = await createAssignItem(payload)
            if (result.error) {
                setSnackbar({ open: true, message: result.error, severity: 'error' })
                selectedItem.quantity += 1 // rollback
            } else {
                setSnackbar({
                    open: true,
                    message: result.message || 'Item assigned successfully',
                    severity: 'success'
                })
                setForm({
                    employeeId: '',
                    category: '',
                    itemId: '',
                    assignmentDate: new Date().toISOString().slice(0, 10),
                    location: '',
                    remarks: ''
                })
            }
        } catch (err) {
            console.error(err)
            setSnackbar({ open: true, message: 'Something went wrong', severity: 'error' })
            selectedItem.quantity += 1
        } finally {
            setLoading(false)
        }
    }

    return (
        <Stack p={2} alignItems="center" spacing={2}>
            <Typography variant="h5" fontWeight="bold">
                Item Assignment
            </Typography>
            <Typography color="text.secondary">Assign inventory items to employees</Typography>

            <Card sx={{ maxWidth: 800, width: '100%', borderRadius: 3, boxShadow: 3 }}>
                <CardContent>
                    <Stack spacing={2} alignItems="center">
                        {/* Employee */}
                        <Divider textAlign="center">
                            <Typography fontWeight="bold">Employee Information</Typography>
                        </Divider>
                        <TextField
                            fullWidth
                            select
                            label="Employee"
                            value={form.employeeId}
                            onChange={(e) => handleChange('employeeId', e.target.value)}
                        >
                            {employees.length === 0 ? (
                                <MenuItem disabled>No employees available</MenuItem>
                            ) : (
                                employees.map((e) => (
                                    <MenuItem key={e.employeeId} value={e.employeeId}>
                                        {e.firstName} {e.lastName}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>

                        {/* Item */}
                        <Divider textAlign="center">
                            <Typography fontWeight="bold">Item Information</Typography>
                        </Divider>
                        <TextField
                            fullWidth
                            select
                            required
                            label="Item Category"
                            value={form.category}
                            onChange={(e) => handleChange('category', e.target.value)}
                        >
                            {categories.length === 0 ? (
                                <MenuItem disabled>No categories</MenuItem>
                            ) : (
                                categories.map((category) => (
                                    <MenuItem key={category.id} value={category.name}>
                                        {category.name}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                        <TextField
                            fullWidth
                            select
                            label="Item / Serial Number"
                            required
                            value={form.itemId}
                            onChange={(e) => handleChange('itemId', e.target.value)}
                        >
                            {items.length === 0 ? (
                                <MenuItem disabled>No items available</MenuItem>
                            ) : (
                                items.map((i) => (
                                    <MenuItem key={i.id} value={i.id}>
                                        {i.brand.toUpperCase()} - {i.model.toUpperCase()} - {i.serialNumber}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                        <TextField
                            fullWidth
                            label="Available Stock"
                            value={items.find((i) => i.id === form.itemId)?.quantity ?? ''}
                            disabled
                            color={(items.find((i) => i.id === form.itemId)?.quantity ?? 0) <= 0 ? 'error' : 'primary'}
                        />

                        {/* Assignment Details */}
                        <Divider textAlign="center">
                            <Typography fontWeight="bold">Assignment Details</Typography>
                        </Divider>
                        <TextField
                            fullWidth
                            type="date"
                            label="Assignment Date"
                            value={form.assignmentDate}
                            onChange={(e) => handleChange('assignmentDate', e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Location"
                            value={form.location}
                            onChange={(e) => handleChange('location', e.target.value)}
                        />
                        <TextField
                            fullWidth
                            label="Remarks"
                            multiline
                            rows={2}
                            value={form.remarks}
                            onChange={(e) => handleChange('remarks', e.target.value)}
                        />

                        {/* Submit */}
                        <Button variant="contained" size="large" onClick={handleSubmit} disabled={loading}>
                            {loading ? 'Assigning...' : 'Assign Item'}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Stack>
    )
}

export default ItemAssignment
